"use client";

import { useEffect, useState } from "react";
import {
  emailFromClientPrincipal,
  displayNameFromClientPrincipal,
  type ClientPrincipalWithClaims,
} from "./clientPrincipal";
import { labsApiHeaders, syncLabsUser, type LabsUserProfile } from "./labsApi";

export type LabsIdentity = {
  /** Canonical Labs user id (MongoDB Users._id). */
  labsUserId: string;
  /** Lowercase email used for deduplication. */
  email: string;
  headers: Record<string, string>;
  /** Profile from the database (IsAdmin, DisplayName, etc.) after sync. */
  profile: LabsUserProfile;
};

type WorkOsUser = {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

function identityFromSync(synced: LabsUserProfile): LabsIdentity {
  return {
    labsUserId: synced.id,
    email: synced.email,
    headers: labsApiHeaders(synced.id, synced.email),
    profile: synced,
  };
}

/**
 * Resolves SWA /.auth/me or WorkOS user to a canonical Labs user id via POST /api/users/sync.
 */
export function useLabsIdentity(workOsUser?: WorkOsUser | null) {
  const [identity, setIdentity] = useState<LabsIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const apiUrl = process.env.NEXT_PUBLIC_API_URL || "";

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!apiUrl) {
        setIdentity(null);
        setLoading(false);
        return;
      }

      if (workOsUser?.id && workOsUser.email) {
        const displayName = workOsUser.firstName
          ? `${workOsUser.firstName}${workOsUser.lastName ? ` ${workOsUser.lastName}` : ""}`
          : workOsUser.email;
        const synced = await syncLabsUser(apiUrl, workOsUser.id, workOsUser.email, displayName);
        if (cancelled) return;
        setIdentity(synced ? identityFromSync(synced) : null);
        setLoading(false);
        return;
      }

      try {
        const authRes = await fetch("/.auth/me");
        const authData = (await authRes.json()) as {
          clientPrincipal?: ClientPrincipalWithClaims | null;
        };
        const principal = authData?.clientPrincipal;
        if (!principal?.userId) {
          const devId = process.env.NEXT_PUBLIC_DEV_USER_ID?.trim();
          if (devId) {
            setIdentity({
              labsUserId: devId,
              email: "",
              headers: labsApiHeaders(devId),
              profile: {
                id: devId,
                email: "",
                displayName: "",
                isAdmin: false,
                isDisabled: false,
                siteAccessApproved: false,
                emailOptOut: false,
              },
            });
          } else {
            setIdentity(null);
          }
          setLoading(false);
          return;
        }

        const email = emailFromClientPrincipal(principal);
        const displayName = displayNameFromClientPrincipal(principal);
        if (email) {
          const synced = await syncLabsUser(apiUrl, principal.userId, email, displayName || email);
          if (cancelled) return;
          if (synced) {
            setIdentity(identityFromSync(synced));
            setLoading(false);
            return;
          }
        }

        setIdentity({
          labsUserId: principal.userId,
          email: email || "",
          headers: labsApiHeaders(principal.userId, email || undefined),
          profile: {
            id: principal.userId,
            email: email || "",
            displayName: displayName || email || "",
            isAdmin: false,
            isDisabled: false,
            siteAccessApproved: false,
            emailOptOut: false,
          },
        });
      } catch {
        if (!cancelled) setIdentity(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, workOsUser?.id, workOsUser?.email, workOsUser?.firstName, workOsUser?.lastName, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  return { identity, loading, reload };
}
